import type { LocaleLanguage } from '../i18n/localeConfig';

const CURRENCY_LOCALES: Record<string, string> = {
  INR: 'en-IN',
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  AED: 'ar-AE',
  SAR: 'ar-SA',
  SGD: 'en-SG',
  AUD: 'en-AU',
  CAD: 'en-CA',
  JPY: 'ja-JP',
  CNY: 'zh-CN',
};

const LANGUAGE_LOCALES: Record<string, string> = {
  en: 'en',
  hi: 'hi-IN',
  ar: 'ar',
  fr: 'fr-FR',
  de: 'de-DE',
  es: 'es-ES',
  pt: 'pt-BR',
  zh: 'zh-CN',
  ja: 'ja-JP',
};

export function getIntlLocale(language: LocaleLanguage | string, currency: string): string {
  if (language === 'hi') return 'hi-IN';
  const langLocale = LANGUAGE_LOCALES[language];
  if (langLocale) {
    if (language === 'en') return CURRENCY_LOCALES[currency] || 'en-IN';
    return langLocale;
  }
  return CURRENCY_LOCALES[currency] || 'en-IN';
}

export function formatMoney(
  amount: number,
  currency: string,
  language: LocaleLanguage | string = 'en',
  options?: Intl.NumberFormatOptions
): string {
  try {
    const intlOpts: Intl.NumberFormatOptions = {
      style: 'currency',
      currency,
      ...options,
    };
    if (currency === 'JPY') {
      intlOpts.minimumFractionDigits = 0;
      intlOpts.maximumFractionDigits = 0;
    } else if (!intlOpts.minimumFractionDigits) {
      intlOpts.minimumFractionDigits = 2;
      intlOpts.maximumFractionDigits = 2;
    }
    return new Intl.NumberFormat(getIntlLocale(language, currency), intlOpts).format(amount ?? 0);
  } catch {
    return `${currency} ${amount ?? 0}`;
  }
}

export function formatLocaleDate(
  date: Date | string,
  language: LocaleLanguage | string = 'en',
  timezone = 'Asia/Kolkata',
  options?: Intl.DateTimeFormatOptions
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(getIntlLocale(language, 'INR'), {
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    ...options,
  }).format(d);
}

export function formatWithDatePattern(
  date: Date | string,
  dateFormat: string,
  language: string,
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = getIntlLocale(language, 'INR');
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options,
  }).formatToParts(d);

  const map: Record<string, string> = {};
  parts.forEach((p) => {
    if (p.type !== 'literal') map[p.type] = p.value;
  });

  if (dateFormat === 'MM/DD/YYYY') {
    return `${map.month}/${map.day}/${map.year}`;
  }
  if (dateFormat === 'YYYY-MM-DD') {
    return `${map.year}-${map.month}-${map.day}`;
  }
  return `${map.day}/${map.month}/${map.year}`;
}
